/* eslint-disable no-nested-ternary */
import {
    lengthConfig,
    validateDescription,
    validateDeveloperName,
    validateEmail,
    validateSmartHomeName,
    validateNote,
    validateURL,
    validateLength,
} from '@paskills/validation';
import { TextInput, RadioButton } from 'lego-on-react';
import block from 'propmods';
import React from 'react';
import * as keyset from 'i18n/SmartHomeForm';
import * as keysetSettingsLabels from 'i18n/SettingsLabels';
import * as keysetSettings from 'i18n/Settings';
import { useI18N } from '@yandex-int/i18n/lib/react';
import i18n from '@yandex-int/i18n';
import { WithSnapshot, withSnapshot } from '../../../context/snapshotContext';
import formContainer from '../../../hocs/form-container';
import { InjectedFormProps } from '../../../hocs/types';
import { routes } from '../../../lib/routes';
import { SmartHomeSettings } from '../../../model/settings';
import { Channel, SmartHome } from '../../../model/skill';
import {
    SmartHomeFormSettings,
    SupportedLanguageFormData,
    ErrorForLanguageForm,
    LanguageCode,
    RegionCode,
} from '../../../types';
import { makeErrorsSameShapeAsValues } from '../../../utils/settings/common';
import { mapSmartHomeSettingsToValues, mapValuesToSmartHomeSettings } from '../../../utils/settings/smart-home';
import EmailField from '../../Form/EmailField/EmailField';
import { Field, Header, CheckboxField } from '../../Form/Form';
import TextArea from '../../Form/TextArea';
import Uploader from '../../Uploader/Uploader';
import Submit from '../Submit';
import { brandVerificationWebsiteCaptionSkill, isTrustedSmartHomeSkillDescription } from './descriptions';
import { smartHomeLabels as l } from './labels';
import * as utils from './utils';
import BackendTypeField from '../../BackendTypeField/BackendTypeField';
import HostsSelect from '../../Hosts/HostsSelect';
import { updateDraft, ApiError } from '../../../api';
import { SkillAccessField } from './SkillAccessField';
import { LanguagesEditComponent } from './LanguagesEditComponent';
import { RegionsSelect } from '../components/RegionsSelect';
import { getIsMultilingualSettingsEnabled, isPrivateSkillsEnabled, getIsYangoRequest } from '../../../utils';

import './SmartHomeForm.scss';

interface OuterProps extends WithSnapshot {
    skillUseZora: boolean;
    logoUrl: string;
    logoColor: string;
    disabled: boolean;
    displayEmailVerificationError: boolean;
    displayBrandVerificationError?: boolean;
    isDraft: boolean;
    settings: SmartHomeSettings;
    isAccountLinkingTemplate: boolean;
    skill: SmartHome;
}

const b = block('SmartHomeForm');

type Props = InjectedFormProps<SmartHomeFormSettings, OuterProps>;

const tLabels = i18n(keysetSettingsLabels);
const t = i18n(keyset);
const tSettings = i18n(keysetSettings);

const SmartHomeFormFields: React.SFC<Props> = (props) => {
    const {
        values,
        isSubmitting,
        errors,
        onChange,
        onCheckboxChange,
        onUploaderChange,
        isValid,
        touched,
        isNonValidationError,
        skill,
        snapshot,
    } = props;
    const t = useI18N(keyset);

    const isEmailSent = props.displayEmailVerificationError && !touched.email;
    const submitMessage = isNonValidationError ? t('unknown-error') : isValid ? '' : t('form-error');
    const submitSeverity = !isValid || isNonValidationError ? 'error' : 'info';

    const displayIsTrustedSmartHomeCheckbox =
        __appConfig.app.displayIsTrustedSmartHomeCheckbox ||
        Boolean(window.sessionStorage.getItem('__display-trusted-checkbox'));

    const displaySkillAccess = isPrivateSkillsEnabled();

    const isYangoRequest = getIsYangoRequest();

    const isMultilingualSettingsEnabled = getIsMultilingualSettingsEnabled();

    return (
        <div {...b()}>
            {isMultilingualSettingsEnabled ? (
                <>
                    <Header>{t('language-and-description')}</Header>
                    <LanguagesEditComponent
                        isDraft={props.isDraft}
                        isTrustedSmartHomeSkill={values.isTrustedSmartHomeSkill}
                        languages={values.supportedLanguages}
                        serializedErrorsForLanguage={errors.supportedLanguages || []}
                        setSupportedLanguages={onChange('supportedLanguages')}
                        disabled={props.disabled || !props.isDraft}
                    />

                    <Header>{t('regions-and-countries')}</Header>
                    <RegionsSelect
                        languages={values.supportedLanguages}
                        enableAllAvailableRegions={values.enableAllAvailableRegions}
                        selectedRegions={values.selectedRegions}
                        disabled={props.disabled || !props.isDraft}
                        regionsWithSufficientLanguages={snapshot.regionsWithSufficientLanguages}
                        setEnableAllAvailableRegions={onChange('enableAllAvailableRegions')}
                        setSelectedRegions={onChange('selectedRegions')}
                        errors={errors.selectedRegions}
                    />
                    <Header>{t('main-settings')}</Header>
                </>
            ) : (
                <>
                    <Header>{t('main-settings')}</Header>
                    <Field name="name" required label={l.name} error={errors.name} caption={t('skill-name')}>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={values.name}
                            onChange={onChange('name')}
                            disabled={props.disabled}
                            autocomplete={false}
                        />
                    </Field>
                </>
            )}

            <BackendTypeField
                skill={skill}
                onBackendTypeChange={onChange('backendType')}
                onFunctionIdChange={onChange('functionId')}
                onWebhookChange={onChange('uri')}
                errors={errors}
                values={values}
                disabled={props.disabled}
            />
            {displaySkillAccess ? (
                <SkillAccessField
                    skillAccess={values.skillAccess}
                    setSkillAccess={onChange('skillAccess')}
                    disabled={props.disabled}
                    channel={Channel.SmartHome}
                />
            ) : (
                <CheckboxField
                    label={l.hideInStore}
                    name="hideInStore"
                    checked={values.hideInStore}
                    disabled={props.disabled}
                    onChange={onCheckboxChange('hideInStore')}
                >
                    Не показывать в каталоге
                </CheckboxField>
            )}
            <Header title={t('publish-in-catalog')} subtitle={t('catalog-description')} />
            {!isMultilingualSettingsEnabled ? (
                <Field name="secondaryTitle" label={l.secondaryTitle} error={errors.secondaryTitle}>
                    <TextArea
                        rows={1}
                        limit={lengthConfig.secondaryTitle}
                        text={values.secondaryTitle}
                        onChange={onChange('secondaryTitle')}
                        disabled={props.disabled}
                    />
                </Field>
            ) : (
                <></>
            )}
            <Field required name="developerName" label={l.developerName} error={errors.developerName}>
                <TextInput
                    theme="normal"
                    size="s"
                    text={values.developerName}
                    onChange={onChange('developerName')}
                    disabled={props.disabled}
                />
            </Field>
            <EmailField
                label={l.email}
                error={errors.email}
                verificationError={props.displayEmailVerificationError ? t('not-confirmed') : undefined}
                value={values.email}
                onChange={onChange('email')}
                disabled={props.disabled}
                isEmailSent={isEmailSent}
                skillId={skill.id}
            />
            {displayIsTrustedSmartHomeCheckbox && (
                <Field
                    label={l.isTrustedSmartHomeSkill}
                    name="isTrustedSmartHomeSkill"
                    description={isTrustedSmartHomeSkillDescription}
                    error={errors.isTrustedSmartHomeSkill}
                >
                    <RadioButton
                        name="isTrustedSmartHomeSkill"
                        theme="normal"
                        size="s"
                        onChange={(e) => onChange('isTrustedSmartHomeSkill')(e.target.value === 'true')}
                        value={String(values.isTrustedSmartHomeSkill)}
                        disabled={props.disabled}
                    >
                        <RadioButton.Radio value="true">{t('yes')}</RadioButton.Radio>
                        <RadioButton.Radio value="false">{t('no')}</RadioButton.Radio>
                    </RadioButton>
                </Field>
            )}
            {!isYangoRequest && (
                <HostsSelect
                    required={values.isTrustedSmartHomeSkill}
                    onHostChange={onChange('brandVerificationWebsite')}
                    disabled={props.disabled}
                    verificationError={props.displayBrandVerificationError ? t('not-confirmed') : undefined}
                    name="brandVerificationWebsite"
                    label={l.brandVerificationWebsite}
                    caption={brandVerificationWebsiteCaptionSkill}
                    documentationUrl={routes.documentation.aliceSkills.publicationSettings}
                    error={errors.brandVerificationWebsite}
                    text={values.brandVerificationWebsite}
                />
            )}

            {!isMultilingualSettingsEnabled ? (
                <Field required name="description" label={l.description} error={errors.description}>
                    <TextArea
                        rows={4}
                        limit={lengthConfig.description}
                        text={values.description}
                        onChange={onChange('description')}
                        disabled={props.disabled}
                    />
                </Field>
            ) : (
                <></>
            )}

            {props.isDraft && (
                <Field
                    name="noteForModerator"
                    label={l.noteForModerator}
                    error={errors.noteForModerator}
                    caption={t('note-for-moderator-caption')}
                >
                    <TextArea
                        rows={4}
                        limit={lengthConfig.noteForModerator}
                        text={values.noteForModerator}
                        onChange={onChange('noteForModerator')}
                        disabled={props.disabled}
                    />
                </Field>
            )}
            <Field required label={l.logoId} error={errors.logoId} caption={t('logo-caption')}>
                <Uploader
                    skillId={skill.id}
                    disabled={props.disabled}
                    url={props.logoUrl}
                    onChange={onUploaderChange}
                    imageId={values.logoId}
                    imageColor={props.logoColor}
                />
            </Field>
            {false && (
                <>
                    <Field
                        name="androidDeepLink"
                        label={l.androidDeepLink}
                        error={errors.androidDeepLink}
                        caption={t('android-deeplink-caption')}
                    >
                        <TextInput
                            theme="normal"
                            size="s"
                            text={values.androidDeepLink}
                            onChange={onChange('androidDeepLink')}
                            disabled={props.disabled}
                        />
                    </Field>
                    <Field
                        name="iosDeepLink"
                        label={l.iosDeepLink}
                        error={errors.iosDeepLink}
                        caption={t('ios-deeplink-caption')}
                    >
                        <TextInput
                            theme="normal"
                            size="s"
                            text={values.iosDeepLink}
                            onChange={onChange('iosDeepLink')}
                            disabled={props.disabled}
                        />
                    </Field>
                    <Field
                        name="iosDeepLinkFallbackURL"
                        label={l.iosDeepLinkFallbackURL}
                        error={errors.iosDeepLinkFallbackURL}
                        caption={t('ios-fallback-caption')}
                    >
                        <TextInput
                            theme="normal"
                            size="s"
                            text={values.iosDeepLinkFallbackURL}
                            onChange={onChange('iosDeepLinkFallbackURL')}
                            disabled={props.disabled}
                        />
                    </Field>
                </>
            )}

            {!props.disabled && (
                <Submit
                    text={t('save')}
                    inProgress={isSubmitting}
                    disabled={!isValid}
                    message={submitMessage}
                    severity={submitSeverity}
                />
            )}
        </div>
    );
};

const SmartHomeForm = formContainer<OuterProps, SmartHomeFormSettings>({
    mapPropsToValues: (props) => mapSmartHomeSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToSmartHomeSettings(values);

            await updateDraft(props.skill.id, settings);
            await props.refreshSnapshot();
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.fields) {
                    // С сервера прилетают ошибки с полей, которых нет в форме чатов, таких как activationPhrases
                    const errors = makeErrorsSameShapeAsValues(err.fields, values);

                    // @ts-ignore
                    await new Promise((resolve) => setErrors(errors, resolve));
                }
            } else {
                console.error(err);
            }

            throw err;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.skill.developerType);
        return {
            developerName: (value: string) => {
                const validateDevName = validateDeveloperName(value, validateAsAdmin, tLabels('developer-name'));
                return {
                    developerName:
                        validateDevName &&
                        t('error-validation-developer-name', {
                            word: validateDevName.split('содержать ')[1],
                        }),
                };
            },
            name: (value: string) => {
                if (getIsMultilingualSettingsEnabled()) {
                    return {};
                }
                const validateDialogName = validateSmartHomeName(value, validateAsAdmin);
                if (validateDialogName.match(/кавычках/)) {
                    return { name: validateDialogName && t('error-validation-quoted-dialog-name') };
                }
                if (validateDialogName.match(/символ/)) {
                    return {
                        name:
                            validateDialogName &&
                            t('error-validation-length-dialog-name', {
                                number: validateDialogName.match(/[0-9]+/),
                            }),
                    };
                }
                return {
                    name:
                        validateDialogName &&
                        t('error-validation-dialog-name', {
                            word: validateDialogName.split('содержать ')[1],
                        }),
                };
            },
            supportedLanguages: (value: SupportedLanguageFormData[]) => {
                if (!getIsMultilingualSettingsEnabled()) {
                    return {};
                }
                const MIN_LENGTH = 2;
                const validationResult: string[] = [];
                const languagesAlreadyPresent = new Set<LanguageCode>();
                let errorIsPresent = false;

                if (value.length === 0) {
                    // never should be the case, but we put an error just in case to block from submitting
                    return { supportedLanguages: [JSON.stringify({})] };
                }

                for (const language of value) {
                    const errorsForLanguageForm: ErrorForLanguageForm = {};

                    // check selected language
                    if (language.languageCode === undefined) {
                        errorsForLanguageForm.languageDisplayName = t('error-validation-language-not-selected');
                    } else if (languagesAlreadyPresent.has(language.languageCode)) {
                        errorsForLanguageForm.languageDisplayName = t('error-validation-language-duplicate');
                    } else {
                        languagesAlreadyPresent.add(language.languageCode);
                    }

                    // todo: check min length & extract

                    if (
                        language.appName === undefined ||
                        language.appName === '' ||
                        language.appName.length < MIN_LENGTH
                    ) {
                        errorsForLanguageForm.appName = t('error-validation-min-length-dialog-name', {
                            number: MIN_LENGTH,
                        });
                    }

                    const validateDialogName = validateSmartHomeName(language.appName || '', validateAsAdmin);

                    if (validateDialogName) {
                        if (validateDialogName.match(/кавычках/)) {
                            errorsForLanguageForm.appName = t('error-validation-quoted-dialog-name');
                        } else if (validateDialogName.match(/символ/)) {
                            errorsForLanguageForm.appName = t('error-validation-max-length-dialog-name', {
                                number: validateDialogName.match(/[0-9]+/),
                            });
                        } else {
                            errorsForLanguageForm.appName = t('error-validation-dialog-name', {
                                word: validateDialogName.split('содержать ')[1],
                            });
                        }
                    }

                    // check externalSettingsDescription
                    if (
                        language.externalSettingsDescription === undefined ||
                        language.externalSettingsDescription === ''
                    ) {
                        errorsForLanguageForm.externalSettingsDescription = t('error-validation-empty-description');
                    } else if (validateDescription(language.externalSettingsDescription)) {
                        errorsForLanguageForm.externalSettingsDescription = t('error-validation-description');
                    }

                    // check supportedUnitsDescription
                    if (language.languageCode !== 'ru' && !(language.supportedUnitsDescription || '').trim()) {
                        errorsForLanguageForm.supportedUnitsDescription = t('error-validation-empty-description');
                    } else if (
                        language.languageCode !== 'ru' &&
                        validateDescription(language.supportedUnitsDescription || '')
                    ) {
                        errorsForLanguageForm.supportedUnitsDescription = t('error-validation-description');
                    }

                    // check supportServiceUrl
                    if (language.supportServiceUrlIsRequired && !language.supportServiceUrl) {
                        errorsForLanguageForm.supportServiceUrl = tSettings('ua-validity-url-req');
                    }
                    if (!!language.supportServiceUrl && validateURL(language.supportServiceUrl)) {
                        errorsForLanguageForm.supportServiceUrl = t('error-validation-url');
                    }

                    // check subtitle
                    if (
                        language.subtitle !== undefined &&
                        validateLength(language.subtitle, lengthConfig.secondaryTitle, tLabels('subtitle'))
                    ) {
                        errorsForLanguageForm.subtitle = t('error-validation-subtitle');
                    }

                    if (Object.keys(errorsForLanguageForm).length !== 0) {
                        errorIsPresent = true;
                    }

                    validationResult.push(JSON.stringify(errorsForLanguageForm));
                }

                // ad-hoc: consider improving framework for such compound "fields".
                return { supportedLanguages: errorIsPresent ? validationResult : undefined };
            },
            selectedRegions: (regions: RegionCode[]) => ({
                // ad-hoc: only one error
                selectedRegions:
                    regions.length === 0 && getIsMultilingualSettingsEnabled()
                        ? [t('error-regions-not-selected')]
                        : undefined,
            }),
            uri: (value: string) => ({
                uri: validateURL(value, props.skillUseZora) && t('error-validation-url'),
            }),
            brandVerificationWebsite: (value: string) => ({
                brandVerificationWebsite: validateURL(value) && t('error-validation-url'),
            }),
            description: (value: string) => ({
                description: getIsMultilingualSettingsEnabled()
                    ? ''
                    : validateDescription(value) && t('error-validation-description'),
            }),
            noteForModerator: (value: string) => ({
                noteForModerator: validateNote(value) && t('error-validation-note-moderator'),
            }),
            email: (value: string) => ({
                email: validateEmail(value) && t('error-validation-email'),
            }),
            secondaryTitle: (value: string) => ({
                secondaryTitle: getIsMultilingualSettingsEnabled()
                    ? ''
                    : validateLength(value, lengthConfig.secondaryTitle, tLabels('subtitle')) &&
                      t('error-validation-subtitle'),
            }),
        };
    },
})(SmartHomeFormFields);

export default withSnapshot(SmartHomeForm);
